import React, { createContext, useContext } from 'react';
import { useRovingTabIndexSetup } from './useRovingTabIndexSetup';
import PropTypes from 'prop-types';

// Context for sharing roving tabindex state across command palette items
/**
 * @typedef {Object} RovingTabIndexContext
 * @property {string} activeId - The id of the active item
 * @property {function} registerItem - Register a new item
 * @property {function} unregisterItem - Unregister an item
 * @property {function} reset - Reset all items and active state
 * @property {function} handleKeyDown - Handle keyboard events for navigation
 * @property {function} isItemActive - Check if an item is active
 * @property {function} getTabIndex - Get tabIndex for an item
 * @property {function} focusFirst - Move focus to the first item
 * @property {function} focusLast - Move focus to the last item
 * @property {function} focusNext - Move focus to the next item
 * @property {function} focusPrevious - Move focus to the previous item
 */

/**
 * @type {import('react').Context<RovingTabIndexContext>}
 */
const RovingTabIndexContext = createContext(null);

/**
 * @type {import('react').FC<{ children: import('react').ReactNode }>}
 */
export const RovingTabIndexContextProvider = ({ children }) => {
  const rovingTabIndex = useRovingTabIndexSetup();
  return (
    <RovingTabIndexContext.Provider value={rovingTabIndex}>
      {children}
    </RovingTabIndexContext.Provider>
  );
};

RovingTabIndexContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook to access the roving tabindex context
 * @returns {RovingTabIndexContext} - Roving tabindex context
 */
export const useRovingTabIndexContext = () => {
  const context = useContext(RovingTabIndexContext);
  if (!context) {
    throw new Error(
      'useRovingTabIndexContext must be used within RovingTabIndexContextProvider',
    );
  }
  return context;
};
